import React, { useState } from "react";
import PropTypes from "prop-types";
import { useFormik } from "formik";
import Button from "~/assets/js/components/Button";
import TextInput from "~/assets/js/components/Forms/TextInput";
import Checkbox from "~/assets/js/components/Forms/Checkbox";
import i18n from "~/assets/js/Helpers/i18n";
import { registerUser } from "~/assets/js/Api/userApi";
import {
    isRequired,
    isEmail,
    isPasswordStrong,
    mustMatch,
    containsNonPrintables,
} from "~/assets/js/Helpers/validators";
import PasswordStrength from "~/assets/js/components/PasswordStrength";
import useAreaCookie from "~/assets/js/Helpers/hooks/useAreaCookie";
import Headline from "~/assets/js/components/Typography/Headline";

const validate = (values) => {
    const errors = {};

    if (containsNonPrintables(values.first_name)) {
        errors.first_name = i18n.t("form.errors.containsNonPrintables");
    }

    if (containsNonPrintables(values.last_name)) {
        errors.last_name = i18n.t("form.errors.containsNonPrintables");
    }

    if (isRequired(values.email)) {
        errors.email = i18n.t("form.errors.required");
    } else if (containsNonPrintables(values.email)) {
        errors.email = i18n.t("form.errors.containsNonPrintables");
    } else if (isEmail(values.email)) {
        errors.email = i18n.t("form.errors.email");
    }

    if (isRequired(values.repeatEmail)) {
        errors.repeatEmail = i18n.t("form.errors.required");
    } else if (mustMatch(values.repeatEmail, values.email)) {
        errors.repeatEmail = i18n.t("form.errors.mustMatch.email");
    }

    if (isRequired(values.password)) {
        errors.password = i18n.t("form.errors.required");
    } else if (containsNonPrintables(values.password)) {
        errors.password = i18n.t("form.errors.containsNonPrintables");
    } else if (isPasswordStrong(values.password)) {
        errors.password = i18n.t("form.errors.notStrong");
    }

    return errors;
};

const RegisterForm = ({ onSubmitResponse }) => {
    const [isLoading, setLoading] = useState(false);
    const cookie = useAreaCookie();

    const formik = useFormik({
        initialValues: {
            first_name: "",
            last_name: "",
            email: "",
            repeatEmail: "",
            password: "",
            AGBs: false,
            address: cookie.address
                ? `${cookie.address.address} ${cookie.address.housenumber}`
                : null,
            postcode: cookie.address ? cookie.address.postcode : null,
            city: cookie.address ? cookie.address.locality : null,
            dispatch_type: cookie.area ? cookie.area.type : null,
        },
        onSubmit: async (values) => {
            setLoading(true);
            // eslint-disable-next-line no-unused-vars
            const { AGBs, repeatEmail, ...userData } = values;
            if (!AGBs) {
                setLoading(false);
                return;
            }
            const response = await registerUser(userData);
            setLoading(false);

            return onSubmitResponse(response);
        },
        validate,
    });

    return (
        <>
            <Headline size={5} resizable>
                {i18n.t("register.title")}
            </Headline>
            <form onSubmit={formik.handleSubmit}>
                <TextInput
                    label={
                        i18n.t("form.label.firstName") +
                        i18n.t("form.label.optional")
                    }
                    bindName="first_name"
                    value={formik.values.first_name}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    errorMessage={formik.errors.first_name}
                    error={
                        formik.touched.first_name && !!formik.errors.first_name
                    }
                />
                <TextInput
                    label={
                        i18n.t("form.label.lastName") +
                        i18n.t("form.label.optional")
                    }
                    bindName="last_name"
                    value={formik.values.last_name}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    errorMessage={formik.errors.last_name}
                    error={
                        formik.touched.last_name && !!formik.errors.last_name
                    }
                />
                <TextInput
                    label={i18n.t("form.label.email")}
                    bindName="email"
                    type="email"
                    value={formik.values.email}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    errorMessage={formik.errors.email}
                    error={formik.touched.email && !!formik.errors.email}
                />
                <TextInput
                    label={i18n.t("form.label.repeatEmail")}
                    bindName="repeatEmail"
                    type="email"
                    value={formik.values.repeatEmail}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    errorMessage={formik.errors.repeatEmail}
                    error={
                        formik.touched.repeatEmail &&
                        !!formik.errors.repeatEmail
                    }
                />
                <TextInput
                    label={i18n.t("form.label.password")}
                    bindName="password"
                    type="password"
                    value={formik.values.password}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    errorMessage={formik.errors.password}
                    error={formik.touched.password && !!formik.errors.password}
                />
                <PasswordStrength password={formik.values.password} />
                <Checkbox
                    bindName="AGBs"
                    value={formik.values.AGBs}
                    label={<AGBs />}
                    onChange={formik.handleChange}
                />
                <Button
                    size="SM"
                    label={i18n.t("register.submit")}
                    type="submit"
                    loading={isLoading}
                    disabled={!formik.values.AGBs}
                />
            </form>
        </>
    );
};

const AGBs = () => {
    const linkAGB = "https://pottsalat.de/agb/";
    const linkPrivacy = "https://pottsalat.de/datenschutz/";

    return (
        <span>
            {i18n.t("form.label.AGBs.firstPart")}
            <a
                href={linkAGB}
                className="gradient-right-text"
                target="_blank"
                rel="noreferrer"
            >
                {i18n.t("form.label.AGBs.linkPart")}
            </a>
            {i18n.t("form.label.AGBs.linkClip")}
            <a
                href={linkPrivacy}
                className="gradient-right-text"
                target="_blank"
                rel="noreferrer"
            >
                {i18n.t("form.label.AGBs.linkPartPrivacy")}
            </a>
            {i18n.t("form.label.AGBs.lastPart")}
        </span>
    );
};

RegisterForm.propTypes = {
    onSubmitResponse: PropTypes.func.isRequired,
};

export default RegisterForm;
